<template>
  <modal v-if="settings && popup_open && popup_name === 'profile-completion-modal'" :closeable="false"
         class="tw-relative"
         modal_name="profile-completion-modal"
         type="xlarge">
    <template v-slot:modal-content>
      <div
        class="primary-c-bg tw--mx-8 tw--mt-8 tw-flex tw-h-[148px] tw-items-center tw-justify-center tw-rounded-t-lg">
        <div class="tw-max-w-[250px]">
          <img :alt="settings.title" :src="settings.logo" onerror="this.src='/img/huddle-logo-yellow.png'" class="max-h-[80px]"/>
        </div>
      </div>
      <div class="tw-px-4">
        <span
          class="tw-mb-3 tw-mt-6 tw-text-lg tw-font-semibold tw-text-black tw-block tw-text-center">{{ $t('login.welcome_to') }} {{ settings.title
          }}</span>
        <p class="tw-mb-8 tw-block tw-text-center">{{ $t('login.complete_profile') }}</p>
        <div class="tw-flex tw-items-center tw-gap-6">
          <div>
            <div
              class="tw-rounded tw-border tw-flex tw-flex-col tw-items-center tw-text-center tw-border-slate-200 tw-px-7 tw-py-7">
              <!-- Input -->
              <label for="fileInputAvatar">
                <input
                  id="fileInputAvatar"
                  ref="fileInput"
                  :accept="accepted_file_types_images"
                  class="hidden"
                  hidden
                  type="file"
                  @change="handleFileChange"/>
              </label>
              <div
                class="tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border-4 tw-border-white tw-overflow-hidden tw-w-[80px] tw-h-[80px] hover-opacity tw-cursor-pointer"
                @click="handleAvatarClick()">
                <img :src="displayed_image" alt="avatar"/>
              </div>
              <span class="tw-mt-2 tw-block tw-text-center tw-text-sm tw-font-semibold tw-text-black">{{ $t('login.profile_picture') }}</span>
              <div class="secondary-c tw-font-bold tw-cursor-pointer hover-opacity tw-mt-1"
                   @click="handleAvatarClick()">
                {{ $t('fields.upload') }}
              </div>
            </div>
          </div>
          <validation-observer ref="modal-profile-completion-modal">
            <div class="tw-flex-1 tw--mt-1">
              <div class="tw-flex tw-gap-4">
                <form-row>
                  <form-field>
                    <form-input v-model="firstname" :autofocus="true" :label="$t('fields.firstname')" name="firstname"
                                :placeholder="$t('login.fill_in_firstname')"
                                rules="required|max:50" @input="setUsername()"></form-input>
                  </form-field>
                </form-row>
                <form-row>
                  <form-field>
                    <form-input v-model="lastname" :label="$t('fields.lastname')" name="lastname" :placeholder="$t('login.fill_in_lastname')"
                                rules="required|max:255" @input="setUsername()"></form-input>
                  </form-field>
                </form-row>
              </div>
              <form-row class="!tw-mb-0">
                <form-field>
                  <form-input v-model="username" :label="$t('fields.username')" name="username" :placeholder="$t('login.at_username')"
                              rules="required|max:50" @keydown="setUsernameChanged()"></form-input>
                  <span v-if="backend_error" class="form-validation-error">{{ backend_error }}</span>
                </form-field>
              </form-row>
            </div>
          </validation-observer>
        </div>
      </div>
    </template>
    <template v-slot:modal-bottom>
      <div class="general-button btn-v2 slim full-width" tabindex="0" @click="handleProfileDetails"
           @keydown.enter="handleProfileDetails">
        {{ $t('admin.confirm') }}
        <i v-if="submitting" class="fal fa-spinner fa-spin"></i>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '@/resources/assets/js/vue/mixins/v2/Modal';
import Global from "../../mixins/Global";
import heic2any from "heic2any";

export default {
  name: 'OnboardingProfileModal',

  mixins: [Modal, Global],

  data() {
    return {
      user,
      accepted_file_types_images: ".jpg, .png, .jpeg, .gif, .bmp, .heic, .heif, .JPG, .PNG, .JPEG, .BMP, .HEIC, .HEIF",
      file: null,
      selected_image_loading: false,
      old_image: null,
      displayed_image: null,
      firstname: "",
      lastname: "",
      username: "",
      username_pristine: true,
      backend_error: false,
      submitting: false,
    };
  },

  methods: {
    checkOpenModal() {
      if (!this.user.onboarded) {
        this.openPopup("profile-completion-modal");
      } else if (user.password_changed_at === null && user.origin !== "fb") {
        this.openPopup("password-setup-modal");
      }
    },

    async handleFileChange(event) {
      const target = event.target;

      if (target.files && target.files[0]) {
        this.file = target.files[0];

        // Most browsers don't support HEIC files, so we need to convert them to png.
        // Server is not yet ready to do the conversion, so for now easiest solution is client side.
        // When server is able to do conversion, this (and the package heic2any) can be deleted
        if (["image/heic", "image/heif"].includes(this.file.type.toLowerCase())) {
          this.selected_image_loading = true;

          const pngFromHeic = await heic2any({blob: this.file, toType: "image/png"});
          this.file = new File(
            [pngFromHeic],
            `${this.file.name.substring(0, this.file.name.lastIndexOf("."))}.png`,
            {
              type: "image/png",
            },
          );
        }

        this.selected_image_loading = false;

        const reader = new FileReader();

        reader.onload = (e) => {
          if (e.target && e.target.result) {
            this.old_image = this.user.avatar;
            this.displayed_image = e.target.result.toString();
          }
        };

        reader.readAsDataURL(this.file);
      }
    },

    handleAvatarClick() {
      if (this.selected_image_loading || this.submitting) {
        return;
      }

      this.$refs.fileInput.click();
    },

    setUsernameChanged() {
      this.username_pristine = false;
    },

    setUsername() {
      if (this.username_pristine) {
        const firstname = this.firstname !== '' ? this.firstname.toLowerCase() : "";
        const lastname = this.lastname !== '' ? this.lastname.toLowerCase() : "";
        const username = `${firstname}${lastname}`;

        // if username doesn't start with @, add it
        if (username !== '') {
          this.username = username.startsWith('@') ? username : `@${username}`;
        }
      }
    },

    async postAvatar() {
      if (this.file !== null) {
        await axios.post(`/api/v3/profile/me/avatar`, {
            avatar: this.file,
          }, {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          },
        );
      }
    },

    async handleProfileDetails() {
      if (!this.submitting) {
        this.submitting = true;
        this.backend_error = false;

        this.validateModal(false).then(async () => {
          try {
            await axios.patch("/api/v4/users/me/account", {
              username: this.username,
              first_name: this.firstname,
              last_name: this.lastname,
              hidden: false,
            });

            await this.postAvatar();

            this.handleProfileSetupModals();
          } catch (e) {
            this.submitting = false;
            const errors = e.response.data.errors;

            // get the first error message
            if (errors) {
              this.backend_error = errors[Object.keys(errors)[0]][0];
            }
          }
        }).catch(() => {
          this.submitting = false;
        });
      }
    },

    handleProfileSetupModals() {
      this.closePopup();

      // user only has to choose a password if he isn't registered from origin and when they do not yet have a password
      if (this.user.password_changed_at === null && this.user.origin !== "fb") {
        this.submitting = false;
        this.openPopup("password-setup-modal");
      } else {
        window.location.reload();
      }
    },
  },

  mounted() {
    this.checkOpenModal();

    this.displayed_image = this.user.avatar;
  },
}
</script>